import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  ScanLine, CheckCircle, AlertTriangle, X, Search, UserCheck,
  Loader2, RotateCcw, Download, WifiOff, Undo2, Camera, Keyboard
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CheckInScanner = ({ events, onCheckedIn }) => {
  const [scanInput, setScanInput] = useState('');
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [checkinCount, setCheckinCount] = useState(0);
  const [lastScannedId, setLastScannedId] = useState(null);
  const [retryEntry, setRetryEntry] = useState(null);
  const [cameraMode, setCameraMode] = useState(false);
  const cameraRef = useRef(null);
  const scannerRef = useRef(null);
  const scanRef = useRef(null);
  const resultTimeout = useRef(null);

  useEffect(() => {
    if (scanRef.current) scanRef.current.focus();
  }, []);

  useEffect(() => {
    if (result) {
      const duration = result.status === 'success' ? 5000 : result.status === 'warning' ? 6000 : 4000;
      resultTimeout.current = setTimeout(() => setResult(null), duration);
      return () => clearTimeout(resultTimeout.current);
    }
  }, [result]);

  // Audio + vibration feedback
  const playSound = useCallback((type) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.value = 0.3;
      if (type === 'success') {
        osc.frequency.value = 880;
        osc.start(); osc.stop(ctx.currentTime + 0.15);
      } else if (type === 'warning') {
        osc.frequency.value = 440;
        osc.start(); osc.stop(ctx.currentTime + 0.3);
      } else {
        osc.frequency.value = 220;
        osc.start(); osc.stop(ctx.currentTime + 0.4);
      }
    } catch {}
    if (navigator.vibrate) {
      navigator.vibrate(type === 'success' ? 200 : type === 'warning' ? [100, 50, 100] : [200, 100, 200]);
    }
  }, []);

  const handleScan = useCallback(async (entryId, method = 'barcode') => {
    let cleaned = entryId.trim();
    const urlMatch = cleaned.match(/\/checkin\/(\d{6}-\d{4})$/);
    if (urlMatch) cleaned = urlMatch[1];
    if (!cleaned || scanning) return;
    setScanning(true);
    setResult(null);
    setRetryEntry(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/events/checkin-scan`,
        { entry_id: cleaned, method }, { withCredentials: true, timeout: 10000 });
      if (res.data.status === 'checked_in') {
        setResult({ status: 'success', message: res.data.message, name: res.data.attendee_name, entry_id: cleaned });
        setCheckinCount(c => c + 1);
        setLastScannedId(cleaned);
        playSound('success');
        if (onCheckedIn) onCheckedIn(res.data);
      } else if (res.data.status === 'already_checked_in') {
        setResult({ status: 'warning', message: res.data.message, name: res.data.attendee_name, entry_id: cleaned });
        playSound('warning');
      }
    } catch (err) {
      if (!err.response) {
        // Network error — offline or timeout
        setResult({ status: 'offline', message: 'Network error — check your connection and try again' });
        setRetryEntry(cleaned);
        playSound('error');
      } else {
        const msg = err.response?.data?.detail || 'Scan failed';
        setResult({ status: 'error', message: msg });
        playSound('error');
      }
    }
    setScanning(false);
    setScanInput('');
    if (scanRef.current) scanRef.current.focus();
  }, [scanning, onCheckedIn, playSound]);

  // Camera scanner start/stop
  const startCamera = useCallback(async () => {
    setCameraMode(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      // Small delay to let the div render
      await new Promise(r => setTimeout(r, 200));
      const scanner = new Html5Qrcode("camera-scanner-region");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        (decodedText) => {
          // Process scan result (camera = QR code)
          handleScan(decodedText, 'qr');
        },
        () => {} // ignore errors (no QR found per frame)
      );
    } catch (err) {
      console.error("Camera error:", err);
      setCameraMode(false);
    }
  }, [handleScan]);

  const stopCamera = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch {}
      scannerRef.current = null;
    }
    setCameraMode(false);
    if (scanRef.current) scanRef.current.focus();
  }, []);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try { scannerRef.current.stop(); } catch {}
      }
    };
  }, []);

  const handleScanKeyDown = (e) => {
    if (e.key === 'Enter' && scanInput.trim()) {
      e.preventDefault();
      handleScan(scanInput);
    }
  };

  // Undo last check-in
  const handleUndo = async (entryId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/events/checkin-undo`,
        { entry_id: entryId }, { withCredentials: true });
      setResult({ status: 'undo', message: res.data.message, name: res.data.attendee_name });
      setCheckinCount(c => Math.max(0, c - 1));
      setLastScannedId(null);
      if (onCheckedIn) onCheckedIn();
    } catch {
      setResult({ status: 'error', message: 'Failed to undo check-in' });
    }
    if (scanRef.current) scanRef.current.focus();
  };

  // Export check-in logs
  const exportLogs = () => {
    const url = selectedEventId
      ? `${BACKEND_URL}/api/admin/events/checkin-logs/export?event_id=${selectedEventId}`
      : `${BACKEND_URL}/api/admin/events/checkin-logs/export`;
    window.open(url, '_blank');
  };

  // Manual search
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const url = selectedEventId
        ? `${BACKEND_URL}/api/admin/events/${selectedEventId}/attendees`
        : `${BACKEND_URL}/api/admin/events/attendees/all`;
      const res = await axios.get(url, { withCredentials: true });
      const q = searchQuery.toLowerCase();
      const filtered = res.data.filter(a =>
        a.name?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q) ||
        a.entry_id?.toLowerCase().includes(q)
      );
      setSearchResults(filtered.slice(0, 20));
    } catch { setSearchResults([]); }
    setSearching(false);
  }, [searchQuery, selectedEventId]);

  useEffect(() => {
    const timer = setTimeout(() => { if (searchQuery.trim()) handleSearch(); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const manualCheckIn = async (attendee) => {
    if (attendee.checked_in) {
      setResult({ status: 'warning', message: `${attendee.name} is already checked in`, name: attendee.name });
      playSound('warning');
      return;
    }
    if (attendee.entry_id) {
      await handleScan(attendee.entry_id, 'manual');
    } else {
      try {
        await axios.put(`${BACKEND_URL}/api/admin/events/attendees/${attendee.id}`,
          { checked_in: true }, { withCredentials: true });
        setResult({ status: 'success', message: `${attendee.name} checked in successfully!`, name: attendee.name });
        setCheckinCount(c => c + 1);
        playSound('success');
        if (onCheckedIn) onCheckedIn({ attendee_name: attendee.name });
      } catch {
        setResult({ status: 'error', message: 'Check-in failed' });
        playSound('error');
      }
    }
    setSearchResults(prev => prev.map(a => a.id === attendee.id ? { ...a, checked_in: true } : a));
  };

  const manualUndoCheckIn = async (attendee) => {
    if (attendee.entry_id) {
      await handleUndo(attendee.entry_id);
    } else {
      try {
        await axios.put(`${BACKEND_URL}/api/admin/events/attendees/${attendee.id}`,
          { checked_in: false }, { withCredentials: true });
        setResult({ status: 'undo', message: `${attendee.name} check-in reversed`, name: attendee.name });
        if (onCheckedIn) onCheckedIn();
      } catch {
        setResult({ status: 'error', message: 'Failed to undo' });
      }
    }
    setSearchResults(prev => prev.map(a => a.id === attendee.id ? { ...a, checked_in: false } : a));
  };

  // Result colors
  const getResultStyles = (status) => {
    switch (status) {
      case 'success': return { bg: 'bg-green-500', text: 'ENTRY APPROVED', icon: <CheckCircle className="w-14 h-14 text-white" /> };
      case 'warning': return { bg: 'bg-amber-500', text: 'ALREADY USED', icon: <AlertTriangle className="w-14 h-14 text-white" /> };
      case 'offline': return { bg: 'bg-gray-700', text: 'CONNECTION LOST', icon: <WifiOff className="w-14 h-14 text-white" /> };
      case 'undo': return { bg: 'bg-blue-500', text: 'CHECK-IN REVERSED', icon: <Undo2 className="w-14 h-14 text-white" /> };
      default: return { bg: 'bg-red-600', text: 'INVALID ENTRY', icon: <X className="w-14 h-14 text-white" /> };
    }
  };

  return (
    <div className="space-y-4" data-testid="checkin-scanner">
      {/* Scanner Header */}
      <div className="bg-[#0A0A0A] rounded-xl p-5 text-white">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-[#BA1826]" />
            <h2 className="font-heading text-base font-bold">Event Check-In Scanner</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)}
              className="text-xs bg-white/10 border border-white/20 text-white rounded px-2 py-1.5 focus:outline-none"
              data-testid="checkin-event-filter">
              <option value="">All Events</option>
              {(events || []).filter(e => e.is_published).map(e => (
                <option key={e.id} value={e.id}>{e.title}</option>
              ))}
            </select>
            <div className="text-xs bg-white/10 px-3 py-1.5 rounded" data-testid="checkin-count">
              {checkinCount} checked in
            </div>
            <button onClick={exportLogs}
              className="text-xs bg-white/10 px-3 py-1.5 rounded hover:bg-white/20 flex items-center gap-1"
              data-testid="export-checkin-logs">
              <Download className="w-3 h-3" /> Export Logs
            </button>
          </div>
        </div>

        {/* Scan Input / Camera Toggle */}
        <div className="flex gap-2 mb-2">
          <button onClick={() => cameraMode ? stopCamera() : startCamera()}
            className={`text-xs px-3 py-2 rounded-lg flex items-center gap-1.5 font-semibold transition-colors ${
              cameraMode ? 'bg-[#BA1826] text-white' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
            data-testid="camera-toggle-btn">
            {cameraMode ? <><Keyboard className="w-3.5 h-3.5" /> Text Input</> : <><Camera className="w-3.5 h-3.5" /> Camera Scan</>}
          </button>
        </div>

        {/* Camera Scanner View */}
        {cameraMode && (
          <div className="rounded-lg overflow-hidden mb-3" data-testid="camera-scanner">
            <div id="camera-scanner-region" ref={cameraRef} style={{ width: '100%' }} />
            <p className="text-[10px] text-gray-500 mt-2 text-center">Point camera at a QR code or barcode</p>
          </div>
        )}

        {/* Text Input (hidden when camera active) */}
        {!cameraMode && (
          <>
            <div className="relative">
              <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={scanRef}
                type="text"
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={handleScanKeyDown}
                placeholder="Scan barcode or type Entry ID (e.g., 022795-1047)..."
                className="w-full pl-10 pr-4 py-3.5 bg-white text-[#0A0A0A] rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#BA1826] placeholder:text-gray-400"
                autoFocus
                autoComplete="off"
                data-testid="scan-entry-input"
              />
              {scanning && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-[#BA1826]" />}
            </div>
            <p className="text-[10px] text-gray-500 mt-2">Scan a barcode or type an Entry ID and press Enter. USB/Bluetooth scanners auto-submit.</p>
          </>
        )}
      </div>

      {/* ====== LARGE RESULT FEEDBACK ====== */}
      {result && (() => {
        const styles = getResultStyles(result.status);
        return (
          <div className={`${styles.bg} rounded-xl p-8 text-center transition-all animate-in fade-in`}
            data-testid="checkin-result">
            <div className="flex justify-center mb-3">{styles.icon}</div>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-wide mb-2" data-testid="checkin-result-label">
              {styles.text}
            </h2>
            {result.name && (
              <p className="text-xl sm:text-2xl text-white/90 font-semibold mb-1">{result.name}</p>
            )}
            <p className="text-sm text-white/70 mb-1">{result.message}</p>
            {result.entry_id && <p className="font-mono text-xs text-white/50">{result.entry_id}</p>}

            <div className="flex justify-center gap-3 mt-4">
              {/* Undo button for successful check-ins */}
              {result.status === 'success' && result.entry_id && (
                <button onClick={() => handleUndo(result.entry_id)}
                  className="bg-white/20 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-white/30 flex items-center gap-1.5"
                  data-testid="undo-checkin-btn">
                  <RotateCcw className="w-3.5 h-3.5" /> Undo
                </button>
              )}
              {/* Retry button for offline/network errors */}
              {result.status === 'offline' && retryEntry && (
                <button onClick={() => handleScan(retryEntry)}
                  className="bg-white/20 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-white/30 flex items-center gap-1.5"
                  data-testid="retry-scan-btn">
                  <RotateCcw className="w-3.5 h-3.5" /> Retry
                </button>
              )}
              <button onClick={() => { setResult(null); if (scanRef.current) scanRef.current.focus(); }}
                className="bg-white/10 text-white/60 px-4 py-2 rounded-lg text-xs hover:bg-white/20">
                Dismiss
              </button>
            </div>
          </div>
        );
      })()}

      {/* Undo Last Check-In (persistent) */}
      {lastScannedId && !result && (
        <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg px-4 py-2.5 flex items-center justify-between">
          <span className="text-xs text-[#4B5563]">Last: <span className="font-mono font-medium">{lastScannedId}</span></span>
          <button onClick={() => handleUndo(lastScannedId)}
            className="text-xs text-[#BA1826] font-semibold hover:underline flex items-center gap-1"
            data-testid="undo-last-btn">
            <RotateCcw className="w-3 h-3" /> Undo Check-In
          </button>
        </div>
      )}

      {/* Manual Search */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
        <h3 className="font-heading text-sm font-bold text-[#0A0A0A] mb-3 flex items-center gap-2">
          <Search className="w-4 h-4 text-[#BA1826]" /> Manual Lookup
        </h3>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, email, or Entry ID..."
          className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none mb-3"
          data-testid="checkin-search-input"
        />
        {searching && <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>}
        {!searching && searchResults.length > 0 && (
          <div className="max-h-72 overflow-y-auto border border-[#E5E7EB] rounded-lg divide-y divide-[#F1F5F9]">
            {searchResults.map(a => (
              <div key={a.id} className="flex items-center justify-between px-4 py-3 hover:bg-[#F8F9FA]" data-testid={`search-result-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-[#0A0A0A] truncate">{a.name}</p>
                    {a.entry_id && (
                      <span className="text-[10px] font-mono bg-[#F1F5F9] text-[#4B5563] px-2 py-0.5 rounded">{a.entry_id}</span>
                    )}
                    {a.checked_in && (
                      <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded">Checked In</span>
                    )}
                  </div>
                  <p className="text-xs text-[#4B5563] truncate">{a.email}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {a.checked_in ? (
                    <button onClick={() => manualUndoCheckIn(a)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 flex items-center gap-1"
                      data-testid={`undo-checkin-${a.id}`}>
                      <RotateCcw className="w-3 h-3" /> Undo
                    </button>
                  ) : (
                    <button onClick={() => manualCheckIn(a)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#BA1826] text-white hover:bg-[#A41623] flex items-center gap-1"
                      data-testid={`manual-checkin-${a.id}`}>
                      <UserCheck className="w-3.5 h-3.5" /> Check In
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {!searching && searchQuery.trim() && searchResults.length === 0 && (
          <p className="text-xs text-[#9CA3AF] text-center py-3">No attendees found matching "{searchQuery}"</p>
        )}
      </div>
    </div>
  );
};

export default CheckInScanner;
